import type { PayloadAction } from '@reduxjs/toolkit';
import { useGetPayGroupId } from 'Modulor/hooks';
import type { Dispatch } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import IntegrationCommunication from 'services/integration-communication';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PlatformsViewport } from 'store/models';
import {
  getPlatformsViewportSelector,
  isFrameFitToViewportSelector,
  isInPopUpSelector,
  setPlatformsViewport,
} from 'store/slices/global';
import { notificationsSelector } from 'store/slices/notifications';
import { isValidJSON } from 'utils/validator';

import { modalRoutesMap } from '../Modulor/modalRoutes';
import {
  isWithdrrawalRejectOpened as isWithdrawalRejectOpenedSelector,
  withdrawalRejectCloseTriggerSelector,
} from '../store/slices/withdrawalReject';
import { inIframe, MODAL_HEIGHT_GAP, PLATFORM_MIN_HEIGHT, WIDGET_HEADER_HEIGHT, WIDGET_HEIGHT_GAP } from '../utils';

const setViewPortData = (e: MessageEvent, dispatch: Dispatch<PayloadAction<PlatformsViewport>>) => {
  const { data } = e;
  if (!isValidJSON(data)) {
    return;
  }

  const preparedData = JSON.parse(data);
  if (preparedData.id === 'hostHeight') {
    dispatch(setPlatformsViewport(preparedData.data));
  }
};

const isModalPath = (pathname: string) => modalRoutesMap.some((r) => pathname.endsWith(r.path));

const getAppHeight = (
  { navBarHeight, headerHeight, modalHeight, height, isOpenedInPopup }: PlatformsViewport,
  applyPlatformHeights?: boolean,
  isModal?: boolean,
) => {
  const rootEl = isModal ? document.getElementById('modal-root') : document.getElementById('root');
  const scrollHeight = rootEl?.scrollHeight ?? 0;
  const rectHeight = rootEl?.getBoundingClientRect().height ?? 0;
  const contentHeight = scrollHeight - rectHeight > 20 ? scrollHeight : rectHeight;
  const openedInIframe = inIframe();
  const platformHeaders = isOpenedInPopup ? navBarHeight : headerHeight + navBarHeight;
  const platformsModalHeight = modalHeight - platformHeaders;
  const contentHeightWithGap = contentHeight + WIDGET_HEIGHT_GAP;

  const platformsViewportHeight = height - platformHeaders;
  const requiredDesktopHeight =
    contentHeight > platformsViewportHeight && !applyPlatformHeights ? contentHeightWithGap : platformsViewportHeight;
  const hasInnerCashier = !!document.querySelector('[title="innerCashier"]');
  const requiredModalHeightInIFrame =
    platformsModalHeight < contentHeightWithGap && !applyPlatformHeights && !hasInnerCashier
      ? contentHeightWithGap
      : platformsModalHeight;
  const requiredHeightInIFrame = isOpenedInPopup
    ? requiredModalHeightInIFrame - (hasInnerCashier ? MODAL_HEIGHT_GAP : 0)
    : requiredDesktopHeight;
  const modalInIframe = isModal && openedInIframe && !isOpenedInPopup;

  if (modalInIframe) return requiredHeightInIFrame + WIDGET_HEADER_HEIGHT;
  return openedInIframe ? requiredHeightInIFrame : contentHeightWithGap;
};

export const useFrameChangeHeight = () => {
  const dispatch = useAppDispatch();
  const payGroupId = useGetPayGroupId();
  const {
    modalHeight = PLATFORM_MIN_HEIGHT,
    headerHeight = 0,
    navBarHeight = 0,
    height = PLATFORM_MIN_HEIGHT,
    pageTop = 0,
  } = useAppSelector(getPlatformsViewportSelector) || {};
  const isOpenedInPopup = useAppSelector(isInPopUpSelector);
  const isWithdrawalRejectOpened = useAppSelector(isWithdrawalRejectOpenedSelector);
  const withdrawalRejectCloseTrigger = useAppSelector(withdrawalRejectCloseTriggerSelector);
  const notifications = useAppSelector(notificationsSelector(payGroupId));
  const isFrameFitToViewport = useAppSelector(isFrameFitToViewportSelector);

  const lastHeightRef = useRef(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const applyPlatformHeights =
    (!withdrawalRejectCloseTrigger ? isWithdrawalRejectOpened : false) || isFrameFitToViewport;

  useLayoutEffect(() => {
    const handler = (e: MessageEvent) => setViewPortData(e, dispatch);
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [dispatch]);

  useEffect(() => {
    const { pathname } = window.location;
    const isModal = isModalPath(pathname);
    const root = isModal ? document.getElementById('modal-root') : document.getElementById('root');
    if (!root) return undefined;
    const sendHeight = () => {
      const requiredAppHeight = getAppHeight(
        {
          modalHeight,
          headerHeight,
          navBarHeight,
          height,
          pageTop,
          isOpenedInPopup,
        },
        applyPlatformHeights,
        isModal,
      );

      if (Math.abs(requiredAppHeight - lastHeightRef.current) > 2) {
        lastHeightRef.current = requiredAppHeight;
        IntegrationCommunication.sendMessage({
          id: 'frameChangedHeight',
          value: requiredAppHeight,
        });
      }
    };

    sendHeight();

    if (typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(() => sendHeight());
      observerRef.current.observe(root);

      return () => observerRef.current?.disconnect();
    }
    const handleResize = () => sendHeight();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [
    notifications,
    modalHeight,
    headerHeight,
    navBarHeight,
    height,
    pageTop,
    isOpenedInPopup,
    isWithdrawalRejectOpened,
    withdrawalRejectCloseTrigger,
    isFrameFitToViewport,
  ]);
};
